import React from 'react'
import PropTypes from 'prop-types'
import Button from 'portal-modules/common/components/Button'
import { useTransition } from 'react-spring'
import { Modal } from '@entrata/portals-common/lib/components'
import {
  portalReskinStyle,
  rootStyle,
  StyledModalDiv,
  StyledWrapper,
} from './rp-modal'
import styled from '@emotion/styled'
import DangerousInnerHTML from 'portal-modules/common/components/DangerousInnerHTML'

const ModStyledModalDiv = styled(StyledModalDiv)`
  position: absolute;
  width: 600px;
  height: 848px;
  background: ${({ theme }) => theme.mode.reskinBackground};
  backdrop-filter: blur(20px);
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.1);
  border-radius: 24px;
  &.remove-pad {
    padding-bottom: 0;
  }

  @media only screen and (max-width: 768px) {
    width: 100%;
    border-radius: 24px 24px 0 0;
    margin: 0;
    height: -webkit-fill-avaeilabl;
  }
`

const ModStyledWrapper = styled(StyledWrapper)`
  display: flex;
  width: 600px;
  height: 848px;

  @media only screen and (max-width: 768px) {
    width: 100%;
    margin: 0;
    height: -webkit-fill-available;
  }
`

const ImageWrapper = styled('div')`
  width: 100%;
  height: 280px;
  border-radius: 24px 24px 0 0;
  background-image: ${({ image_url }) => `url(${image_url})`};
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`

const HeadingTitle = styled('h3')`
  fontfamily: ${({ theme }) => theme.fonts.body};
  font-weight: 700;
  font-size: 21px;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.mode.text_color};
  margin: 40px 48px 24px 46px;
  display: flex;
  justify-content: flex-start;
  @media only screen and (max-width: 768px) {
    margin: 24px 16px 24px 16px;
  }
`

const SubHeadingTitle = styled('h6')`
  fontfamily: ${({ theme }) => theme.fonts.body};
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.mode.text_color};
  height: 21px;
  margin: 0 48px 16px 46px;
  display: flex;
  justify-content: flex-start;
  @media only screen and (max-width: 768px) {
    margin: 0 16px 24px 16px;
  }
`

const HeadingDescription = styled('p')`
  font-weight: 400;
  font-size: 13px;
  letter-spacing: 0.25px;
  color: ${({ theme }) => theme.gray.mediumlight};
  margin: 0 48px;
  height: ${({ image_url }) => (image_url ? '341px' : '620px')};
  overflow-y: auto;
  overflow-x: auto;
  @media only screen and (max-width: 768px) {
    margin: 0 16px;
  }
`
const ButtonWrapper = styled('div')`
  display: flex;
  justify-content: center;
  margin: 24px 0 40px 0;
  @media only screen and (max-width: 768px) {
    margin-left: 18px;
  }
`

const StyledDangerousInnerHTML = styled(DangerousInnerHTML)`
  text-align: ${({ isImage }) => (isImage ? 'center' : 'left')};
`

const StyledButton = styled(Button)`
  @media only screen and (max-width: 768px) {
    width: auto;
    min-width: 100px;
    height: 48px;
    padding: 14px 28px;
    font-size: 16px;
  }
`

const ReskinRpModal = ({
  blurId,
  hideCloseButton,
  onClose,
  onClick,
  image_url,
  rootId,
  show,
  title,
  subtitle,
  content,
  numberOfButtons,
  actionButtonType,
  actionButtonLabel,
  closeButtonType,
  closeButtonLabel,
}) => {
  const toggleRoot = action => {
    const rootElement = document.getElementById(rootId)
    const blurElement = document.getElementById(blurId)
    const blurReskinElement = document.getElementById('bg-overlay')
    if (!blurReskinElement?.classList?.contains('blur')) {
      rootElement && rootElement.classList[action](rootStyle)
      blurElement && blurElement.classList[action]('blur')
    }
  }
  React.useEffect(() => {
    toggleRoot(show ? 'add' : 'remove')
    return function removeRootStyles() {
      toggleRoot('remove')
    }
  }, [show])

  const transitions = useTransition(show, null, {
    from: { opacity: 0, transform: 'translateY(100px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    leave: { opacity: 0, transform: 'translateY(-200px)' },
  })

  return transitions.map(
    ({ item, key, props }) =>
      item && (
        <Modal className={portalReskinStyle()} key={key} onClose={onClose}>
          {({ containerProps, titleId }) => (
            <ModStyledWrapper style={{ ...props }}>
              <ModStyledModalDiv
                className={!hideCloseButton ? 'remove-pad' : ''}
                {...containerProps}
              >
                {image_url ? <ImageWrapper image_url={image_url} /> : <></>}
                <HeadingTitle id={titleId}>{title}</HeadingTitle>
                {subtitle ? (
                  <SubHeadingTitle>{subtitle}</SubHeadingTitle>
                ) : (
                  <></>
                )}
                <HeadingDescription image_url={image_url}>
                  <StyledDangerousInnerHTML isImage={content?.includes('<img')}>
                    {content}
                  </StyledDangerousInnerHTML>
                </HeadingDescription>
                <ButtonWrapper>
                  {numberOfButtons == 1 ? (
                    <StyledButton
                      appearance={actionButtonType}
                      size='medium'
                      onClick={onClick}
                    >
                      {__(actionButtonLabel)}
                    </StyledButton>
                  ) : (
                    <StyledButton
                      appearance={closeButtonType}
                      size='medium'
                      onClick={onClose}
                    >
                      {__(closeButtonLabel)}
                    </StyledButton>
                  )}
                </ButtonWrapper>
              </ModStyledModalDiv>
            </ModStyledWrapper>
          )}
        </Modal>
      ),
  )
}

ReskinRpModal.propTypes = {
  /** ID of the background to be blurred  */
  blurId: PropTypes.string,
  /** Custom text for close button */
  buttonLabel: PropTypes.string,
  /** You can directly pass some string content to show to the users */
  children: PropTypes.any,
  /** To toggle visibility of Close button */
  hideCloseButton: PropTypes.bool,
  /** onClose method, provided by the modal control */
  onClose: PropTypes.func.isRequired,
  /** ID of the root element */
  rootId: PropTypes.string,
  /** A flas to show/hide modal. Provided by modal control */
  show: PropTypes.bool,
  /** Title of the modal  */
  title: PropTypes.string,
  /** Title of the modal  */
  subtitle: PropTypes.string,
  numberOfButtons: PropTypes.number,
  buttonLabels: PropTypes.arrayOf(PropTypes.string),
  buttonTypes: PropTypes.arrayOf(PropTypes.string),
}
ReskinRpModal.defaultProps = {
  blurId: 'bg-img',
  buttonLabel: __('Close'),
  rootId: 'root',
  subtitle: '',
  actionButtonType: 'primary',
  actionButtonLabel: __('Close'),
  closeButtonType: 'secondary',
  closeButtonLabel: __('Close'),
  numberOfButtons: 1,
}

export default ReskinRpModal
